const BASE64_IMAGE_SIGNATURES = [
  '/9j/',
  'iVBORw0KGgo',
  'R0lGODlh',
  'R0lGODdh',
  'UklGRg',
  'Qk0=',
];

const MIME_TYPE_TO_EXTENSION: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/bmp': 'bmp',
};

const isBase64ImageData = (data: string): boolean => {
  const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
  if (!base64Regex.test(data)) return false;

  return BASE64_IMAGE_SIGNATURES.some((signature) =>
    data.startsWith(signature)
  );
};

const detectImageFormatFromBase64 = (base64Data: string): string => {
  if (base64Data.startsWith('iVBORw0KGgo')) return 'image/png';
  if (base64Data.startsWith('R0lGODlh') || base64Data.startsWith('R0lGODdh'))
    return 'image/gif';
  if (base64Data.startsWith('/9j/')) return 'image/jpeg';
  if (base64Data.startsWith('UklGRg')) return 'image/webp';
  if (base64Data.startsWith('Qk0=')) return 'image/bmp';

  return 'image/jpeg';
};

const detectImageFormatFromBinary = (arrayBuffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(arrayBuffer);

  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return 'image/jpeg';
  }
  if (
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47
  ) {
    return 'image/png';
  }
  if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) {
    return 'image/gif';
  }
  if (
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46
  ) {
    return 'image/webp';
  }
  if (bytes[0] === 0x42 && bytes[1] === 0x4d) {
    return 'image/bmp';
  }

  return 'image/jpeg';
};

const getFileExtensionFromMimeType = (mimeType: string): string => {
  return MIME_TYPE_TO_EXTENSION[mimeType] || 'png';
};

const ensureCorrectExtension = (fileName: string, mimeType: string): string => {
  const correctExtension = getFileExtensionFromMimeType(mimeType);
  const fileNameWithoutExtension = fileName.replace(/\.[^/.]+$/, '');
  const hasExtension = /\.[^/.]+$/.test(fileName);

  if (!hasExtension) {
    return `${fileName}.${correctExtension}`;
  }

  const currentExtension = fileName.split('.').pop()?.toLowerCase();
  const validExtensions = Object.values(MIME_TYPE_TO_EXTENSION);

  if (!validExtensions.includes(currentExtension || '')) {
    return `${fileNameWithoutExtension}.${correctExtension}`;
  }

  if (currentExtension !== correctExtension) {
    return `${fileNameWithoutExtension}.${correctExtension}`;
  }

  return fileName;
};

const toDataURL = async (url: string): Promise<[string, string]> => {
  const response = await fetch(url);
  const responseClone = response.clone();
  const text = await response.text();

  if (isBase64ImageData(text)) {
    const mimeType = detectImageFormatFromBase64(text);
    const dataURL = `data:${mimeType};base64,${text}`;
    return [dataURL, mimeType];
  }

  const blob = await responseClone.blob();
  let mimeType = blob.type;

  if (mimeType === 'application/octet-stream' || !mimeType) {
    const arrayBuffer = await blob.arrayBuffer();
    mimeType = detectImageFormatFromBinary(arrayBuffer);

    const correctedBlob = new Blob([arrayBuffer], { type: mimeType });
    return [URL.createObjectURL(correctedBlob), mimeType];
  }

  return [URL.createObjectURL(blob), mimeType];
};

export const downloadImageFromUrl = async (url: string, fileName?: string) => {
  const a = document.createElement('a');
  const [dataURL, mimeType] = await toDataURL(url);
  a.href = dataURL;

  // Generate filename with correct extension
  if (!fileName) {
    const extension = getFileExtensionFromMimeType(mimeType);
    a.download = `Social pack image.${extension}`;
  } else {
    a.download = ensureCorrectExtension(fileName, mimeType);
  }

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // this is a way to wait for image to actually be downloaded
  await Promise.resolve();
};
